import { next } from "@ember/runloop";
import { withPluginApi } from "discourse/lib/plugin-api";
import { CREATE_TOPIC } from "discourse/models/composer";
import { i18n } from "discourse-i18n";
import CommunityFeedbackComposerFields from "../components/community-feedback-composer-fields";
import CommunityFeedbackGallery from "../components/community-feedback-gallery";
import CommunityFeedbackTopicStatus from "../components/community-feedback-topic-status";

const FEEDBACK_MAPPING_KEY = "category.mod.feedback";
const WISH_MAPPING_KEY = "category.mod.wishes";

function composerModel(value, composer) {
  return value?.model || value || composer.model;
}

function clearCommunityMode(model, composer) {
  if (!model?.communityFeedbackItemType) {
    return;
  }

  model.setProperties({
    communityFeedbackPublishing: false,
    communityFeedbackItemType: null,
    communityFeedbackPublishLabel: null,
    communityFeedbackGames: [],
    communityFeedbackGameKey: null,
    communityFeedbackCustomFields: {},
  });
  composer.disableSubmit = false;
}

export async function syncCommunityMode(
  value,
  composer,
  navigation,
  siteSettings
) {
  const model = composerModel(value, composer);
  const mappingKey =
    model?.category?.custom_fields?.community_navigation_key;
  const itemType =
    model?.action === CREATE_TOPIC &&
    siteSettings.community_feedback_enabled &&
    mappingKey === FEEDBACK_MAPPING_KEY
      ? "feedback"
      : model?.action === CREATE_TOPIC &&
          siteSettings.community_wishes_enabled &&
          mappingKey === WISH_MAPPING_KEY
        ? "wish"
        : null;

  if (!itemType) {
    clearCommunityMode(model, composer);
    return;
  }

  const tree = await navigation.load();
  const games =
    tree.primary.find((item) => item.key === "games")?.children?.map(
      ({ game_key: key, label: name }) => ({ key, name })
    ) || [];
  const expectedMappingKey =
    itemType === "wish" ? WISH_MAPPING_KEY : FEEDBACK_MAPPING_KEY;
  if (
    model.category?.custom_fields?.community_navigation_key !==
    expectedMappingKey
  ) {
    return;
  }

  model.setProperties({
    communityFeedbackPublishing: true,
    communityFeedbackItemType: itemType,
    communityFeedbackPublishLabel:
      itemType === "wish"
        ? "community_feedback.wishes.publish_action"
        : "community_feedback.publish_action",
    communityFeedbackGames: games,
    communityFeedbackGameKey: model.communityFeedbackGameKey || null,
    communityFeedbackCustomFields: model.communityFeedbackGameKey
      ? { community_feedback_game_key: model.communityFeedbackGameKey }
      : {},
  });
  composer.disableSubmit = !model.communityFeedbackGameKey;
}

export default {
  name: "community-feedback",

  initialize(container) {
    const siteSettings = container.lookup("service:site-settings");
    if (
      !siteSettings.community_feedback_enabled &&
      !siteSettings.community_wishes_enabled
    ) {
      return;
    }

    const appEvents = container.lookup("service:app-events");
    const composer = container.lookup("service:composer");
    const navigation = container.lookup("service:community-navigation");

    appEvents.on("composer:category-changed", (model) => {
      void syncCommunityMode(model, composer, navigation, siteSettings);
    });
    appEvents.on("composer:opened", () => {
      void syncCommunityMode(
        composer.model,
        composer,
        navigation,
        siteSettings
      );
    });
    appEvents.on("composer:closed", () => {
      if (composer.model?.communityFeedbackItemType) {
        next(() => (composer.disableSubmit = false));
      }
    });
    appEvents.on("composer:cancelled", () => {
      clearCommunityMode(composer.model, composer);
    });
    appEvents.on("composer:created-post", () => {
      clearCommunityMode(composer.model, composer);
    });

    withPluginApi((api) => {
      api.addModelField("composer", "communityFeedbackPublishing");
      api.addModelField("composer", "communityFeedbackItemType");
      api.addModelField("composer", "communityFeedbackPublishLabel");
      api.addModelField("composer", "communityFeedbackGames");
      api.addModelField("composer", "communityFeedbackGameKey");
      api.addModelField("composer", "communityFeedbackCustomFields");
      api.serializeOnCreate(
        "topic_custom_fields",
        "communityFeedbackCustomFields"
      );
      api.serializeToDraft("communityFeedbackGameKey");
      api.serializeToDraft("communityFeedbackItemType");
      api.serializeToDraft("communityFeedbackPublishing");

      api.customizeComposerText({
        actionTitle(model) {
          if (model.communityFeedbackPublishLabel) {
            return i18n(model.communityFeedbackPublishLabel);
          }
        },
        saveLabel(model) {
          return model.communityFeedbackPublishLabel;
        },
        titlePlaceholder(model) {
          if (model.communityFeedbackItemType === "wish") {
            return "community_feedback.wishes.composer.title_placeholder";
          }
          if (model.communityFeedbackItemType === "feedback") {
            return "community_feedback.composer.title_placeholder";
          }
        },
      });
      api.registerValueTransformer(
        "composer-editor-reply-placeholder",
        ({ value, context }) => {
          const model = context.model?.model || context.model;
          if (model?.communityFeedbackItemType === "wish") {
            return "community_feedback.wishes.composer.description_placeholder";
          }
          if (model?.communityFeedbackItemType === "feedback") {
            return "community_feedback.composer.description_placeholder";
          }
          return value;
        }
      );

      if (siteSettings.community_feedback_enabled) {
        api.renderInOutlet(
          "community-navigation-media-feedback-page",
          CommunityFeedbackGallery
        );
      }
      api.renderInOutlet("composer-fields", CommunityFeedbackComposerFields);
      api.renderInOutlet("topic-title", CommunityFeedbackTopicStatus);
    });
  },
};
